// src/features/simulators/kiosk/components/KioskConfigForm.tsx

import React from 'react';
import {
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import {
  Route as RouteIcon,
  VpnKey as VpnKeyIcon,
  Checklist as ChecklistIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { ReservationNumberControl } from '../../shared';
import { KeyDeliveryMethod, SectionCode } from '../../shared/types/journey.types';

export type KioskJourneyType =
  | 'new_checkin'
  | 'continue_precheckin'
  | 'walking_guest'
  | 'hybrid_abandonment';

export interface KioskConfigValues {
  journeyType: KioskJourneyType;
  keyDeliveryMethod: KeyDeliveryMethod;
  sectionsToProcess: SectionCode[];
  reservationNumber: string;
  autoGenerateReservation: boolean;
}

interface KioskConfigFormProps {
  values: KioskConfigValues;
  onChange: (values: KioskConfigValues) => void;
}

const SECTION_OPTIONS: SectionCode[] = [
  SectionCode.S,
  SectionCode.R,
  SectionCode.I,
  SectionCode.F,
  SectionCode.U,
  SectionCode.P,
];

export const KioskConfigForm: React.FC<KioskConfigFormProps> = ({
  values,
  onChange,
}) => {
  const translate = useTranslate();

  const update = (partial: Partial<KioskConfigValues>) => {
    const next = { ...values, ...partial };

    // Walking guest always includes section R
    if (next.journeyType === 'walking_guest' && !next.sectionsToProcess.includes(SectionCode.R)) {
      next.sectionsToProcess = [SectionCode.R, ...next.sectionsToProcess.filter(s => s !== SectionCode.R)];
    }

    onChange(next);
  };

  const toggleSection = (section: SectionCode) => {
    // Walking guest: R is mandatory
    if (section === SectionCode.R && values.journeyType === 'walking_guest') return;

    const current = values.sectionsToProcess;
    const next = current.includes(section)
      ? current.filter(s => s !== section)
      : [...current, section];
    update({ sectionsToProcess: next });
  };

  const getSectionLabel = (section: SectionCode): string => {
    const labels: Record<string, string> = {
      [SectionCode.S]: translate('resources.simulators.kiosk.sections.search'),
      [SectionCode.R]: translate('resources.simulators.kiosk.sections.room_selection'),
      [SectionCode.I]: translate('resources.simulators.kiosk.sections.identification'),
      [SectionCode.F]: translate('resources.simulators.kiosk.sections.signature'),
      [SectionCode.U]: translate('resources.simulators.kiosk.sections.upselling'),
      [SectionCode.P]: translate('resources.simulators.kiosk.sections.payment'),
    };
    return labels[section] || section;
  };

  return (
    <Grid container spacing={3}>
      {/* Journey Type */}
      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.kiosk.journey_type.label')}
          icon={<RouteIcon />}
          variant="outlined"
        >
          <FormControl fullWidth size="small">
            <InputLabel>{translate('resources.simulators.kiosk.journey_type.label')}</InputLabel>
            <Select
              value={values.journeyType}
              onChange={(e) => update({ journeyType: e.target.value as KioskJourneyType })}
              label={translate('resources.simulators.kiosk.journey_type.label')}
            >
              <MenuItem value="new_checkin">
                {translate('resources.simulators.kiosk.journey_type.new_checkin')}
              </MenuItem>
              <MenuItem value="continue_precheckin">
                {translate('resources.simulators.kiosk.journey_type.continue_precheckin')}
              </MenuItem>
              <MenuItem value="walking_guest">
                {translate('resources.simulators.kiosk.journey_type.walking_guest')}
              </MenuItem>
              <MenuItem value="hybrid_abandonment">
                {translate('resources.simulators.kiosk.journey_type.hybrid_abandonment')}
              </MenuItem>
            </Select>
          </FormControl>

          {values.journeyType !== 'walking_guest' && (
            <ReservationNumberControl
              value={values.reservationNumber}
              autoGenerate={values.autoGenerateReservation}
              onValueChange={(value) => update({ reservationNumber: value })}
              onAutoGenerateChange={(autoGenerate) => update({ autoGenerateReservation: autoGenerate })}
              prefix="RES"
            />
          )}
        </MyFormSection>
      </Grid>

      {/* Key Delivery Method */}
      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.kiosk.key_delivery.label')}
          icon={<VpnKeyIcon />}
          variant="outlined"
        >
          <FormControl fullWidth size="small">
            <InputLabel>{translate('resources.simulators.kiosk.key_delivery.label')}</InputLabel>
            <Select
              value={values.keyDeliveryMethod}
              onChange={(e) => update({ keyDeliveryMethod: e.target.value as KeyDeliveryMethod })}
              label={translate('resources.simulators.kiosk.key_delivery.label')}
            >
              <MenuItem value={KeyDeliveryMethod.MAGNETIC_CARD}>
                {translate('resources.simulators.kiosk.key_delivery.magnetic_card')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.RFID_CARD}>
                {translate('resources.simulators.kiosk.key_delivery.rfid_card')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.CONVENTIONAL_KEY}>
                {translate('resources.simulators.kiosk.key_delivery.conventional_key')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_PIN}>
                {translate('resources.simulators.kiosk.key_delivery.digital_pin')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_MOBILE}>
                {translate('resources.simulators.kiosk.key_delivery.digital_mobile')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.QR_DISPENSER}>
                {translate('resources.simulators.kiosk.key_delivery.qr_dispenser')}
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.MANUAL}>
                {translate('resources.simulators.kiosk.key_delivery.manual')}
              </MenuItem>
            </Select>
          </FormControl>
        </MyFormSection>
      </Grid>

      {/* Sections to Process */}
      <Grid item xs={12}>
        <MyFormSection
          title={translate('resources.simulators.kiosk.sections.to_process')}
          icon={<ChecklistIcon />}
          variant="outlined"
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {SECTION_OPTIONS.map((section) => {
              const isChecked = values.sectionsToProcess.includes(section);
              const isDisabled =
                (section === SectionCode.R && values.journeyType === 'walking_guest'); // forced on

              return (
                <FormControlLabel
                  key={section}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={() => toggleSection(section)}
                      disabled={isDisabled}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {section} - {getSectionLabel(section)}
                    </Typography>
                  }
                  sx={{ minWidth: 180 }}
                />
              );
            })}
          </Box>
        </MyFormSection>
      </Grid>
    </Grid>
  );
};
